/**
 * m3 echo Frontend - Model Comparison Detail Dialog
 *
 * Dialog showing side-by-side comparison of Layer 2 analysis results.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  modelComparisonApi,
  ComparisonResultDetail,
  ComparisonRunResponse,
} from '../../services/admin';

interface Props {
  open: boolean;
  onClose: () => void;
  resultId: number | null;
  run: ComparisonRunResponse | null;
}

/**
 * Highlights search terms in the response text
 */
const highlightSearchTerms = (text: string, searchTerms: string[]): JSX.Element => {
  if (!searchTerms || searchTerms.length === 0) {
    return <>{text}</>;
  }

  // Create regex pattern for all search terms (case insensitive)
  const pattern = new RegExp(
    `(${searchTerms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'gi'
  );

  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = searchTerms.some(
          (term) => part.toLowerCase() === term.toLowerCase()
        );
        return isMatch ? (
          <Box
            component="span"
            key={index}
            sx={{
              backgroundColor: 'warning.light',
              color: 'warning.contrastText',
              fontWeight: 'bold',
              px: 0.5,
              borderRadius: 0.5,
            }}
          >
            {part}
          </Box>
        ) : (
          <span key={index}>{part}</span>
        );
      })}
    </>
  );
};

interface AnalysisColumnProps {
  title: string;
  analysis: {
    mentioned: boolean;
    position: number | null;
    matched_term: string | null;
    context: string | null;
    sentiment: string | null;
    champion_name: string | null;
  };
  score: number;
  matchesBaseline?: boolean;
  isBaseline?: boolean;
}

const AnalysisColumn: React.FC<AnalysisColumnProps> = ({
  title,
  analysis,
  score,
  matchesBaseline,
  isBaseline = false,
}) => {
  const getSentimentColor = (sentiment: string | null) => {
    switch (sentiment) {
      case 'positive':
        return 'success';
      case 'negative':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        backgroundColor: isBaseline ? 'grey.50' : 'background.paper',
        border: matchesBaseline === false ? '2px solid' : '1px solid',
        borderColor: matchesBaseline === false ? 'error.main' : 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          {title}
        </Typography>
        {!isBaseline && matchesBaseline !== undefined && (
          matchesBaseline ? (
            <CheckCircleIcon fontSize="small" color="success" />
          ) : (
            <CancelIcon fontSize="small" color="error" />
          )
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Score */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Score
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {score}
          </Typography>
        </Box>

        {/* Mentioned */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Erwähnt
          </Typography>
          <Box>
            <Chip
              label={analysis.mentioned ? 'Ja' : 'Nein'}
              size="small"
              color={analysis.mentioned ? 'success' : 'default'}
            />
          </Box>
        </Box>

        {/* Position */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Position
          </Typography>
          <Typography variant="body1">
            {analysis.position ?? '-'}
          </Typography>
        </Box>

        {/* Sentiment */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Sentiment
          </Typography>
          <Box>
            {analysis.sentiment ? (
              <Chip
                label={analysis.sentiment}
                size="small"
                color={getSentimentColor(analysis.sentiment) as any}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </Box>
        </Box>

        {/* Champion */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Zuerst genannte Firma (Pos. 1; Marken/Produkte technisch eingeschlossen)
          </Typography>
          <Typography variant="body2">
            {analysis.champion_name || '-'}
          </Typography>
        </Box>

        {/* Matched Term */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Gefundener Begriff
          </Typography>
          <Typography variant="body2">
            {analysis.matched_term || '-'}
          </Typography>
        </Box>

        {/* Context */}
        {analysis.context && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Kontext
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                fontStyle: 'italic',
                color: 'text.secondary',
                maxHeight: 80,
                overflow: 'auto',
              }}
            >
              "{analysis.context}"
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const ModelComparisonDetailDialog: React.FC<Props> = ({
  open,
  onClose,
  resultId,
  run: _run,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [detail, setDetail] = useState<ComparisonResultDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && resultId) {
      loadDetail();
    }
  }, [open, resultId]);

  const loadDetail = async () => {
    if (!resultId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await modelComparisonApi.getResultDetail(resultId);
      setDetail(response.data);
    } catch (err: any) {
      console.error('Error loading detail:', err);
      setError(err.response?.data?.detail || 'Fehler beim Laden der Details');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDetail(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { minHeight: '80vh' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Vergleichsdetail - Job Result #{detail?.job_result_id || resultId}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : detail ? (
          <Box>
            {/* Metadata */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Projekt
                  </Typography>
                  <Typography variant="body1">
                    {detail.project_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Layer 1 Modell
                  </Typography>
                  <Typography variant="body1">
                    {detail.layer1_model || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Suchbegriffe
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {detail.search_terms.map((term, i) => (
                      <Chip
                        key={i}
                        label={term}
                        size="small"
                        color={i === 0 ? 'primary' : 'default'}
                        variant={i === 0 ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Layer 1 Response */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.100' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Layer 1 Response (von {detail.layer1_model}):
              </Typography>
              <Box
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  p: 1,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                }}
              >
                {highlightSearchTerms(detail.raw_response, detail.search_terms)}
              </Box>
            </Paper>

            {/* Comparison Columns */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Layer 2 Analyse Vergleich
              {detail.model1_matches_model2 !== null &&
                ` · Arm 1 ↔ Arm 2: ${detail.model1_matches_model2 ? 'gleicher Score' : 'Score weicht ab'}`}
            </Typography>

            <Grid container spacing={2}>
              {/* Baseline (Live) */}
              <Grid item xs={12} md={detail.model2_analysis ? 4 : 6}>
                <AnalysisColumn
                  title="Gespeichert (informativ)"
                  analysis={detail.baseline_analysis}
                  score={detail.baseline_score}
                  isBaseline
                />
              </Grid>

              {/* Model 1 */}
              <Grid item xs={12} md={detail.model2_analysis ? 4 : 6}>
                <AnalysisColumn
                  title={detail.variant_1}
                  analysis={detail.model1_analysis}
                  score={detail.model1_score}
                  matchesBaseline={detail.model1_matches_baseline}
                />
              </Grid>

              {/* Model 2 (optional) */}
              {detail.model2_analysis && (
                <Grid item xs={12} md={4}>
                  <AnalysisColumn
                    title={detail.variant_2 || 'Arm 2'}
                    analysis={detail.model2_analysis}
                    score={detail.model2_score || 0}
                    matchesBaseline={detail.model2_matches_baseline || false}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModelComparisonDetailDialog;
